<script lang="ts" setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useI18n } from 'vue-i18n'
import Popper from 'vue3-popper'
import {
  DBtn,
  DTextField,
  DTable,
  DModal,
  DLoading,
  SearchMdIcon,
  Trash03Icon,
  Edit03Icon
} from 'front-library'
import {
  generateReportList,
  getReportRequest
} from '@/app/core/services/reports'
import {
  LiquidationRequest,
  deleteLiquidationRequest,
  getLiquidationRequests
} from '@/app/core/services/liquidation'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const typeAlertModal = ref('')

const loading = ref(false)
const messageLoading = ref('')

/* Datos */
const search = ref()
const tableLiquidationRequest = ref(null)
const liquidationRequestForDelete = ref()
const isDelete = ref(false)
const urlFile = ref(null)
const refresh = ref()
const payload = ref()

/* Datos componente table */
const columns = ref([
  {
    name: 'leave_reason',
    title: t('liquidations.list.leaveReason'),
    table: 'leave_reason'
  },
  {
    name: 'leave_date',
    title: t('liquidations.list.leaveDate'),
    table: 'leave_date'
  },
  {
    name: 'listEmployees',
    title: t('liquidations.list.employees'),
    table: 'employees'
  },
  {
    name: 'status',
    title: t('liquidations.list.status'),
    table: 'status_code'
  },
  {
    name: 'updated_at',
    title: t('liquidations.list.lastUpdate'),
    table: 'liquidation_request.updated_at'
  },
  { name: 'actions', title: '' }
])
const rows = ref<LiquidationRequest[]>([])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const sortBy = ref('')

/* Redirect's */
const createLiquidationRequest = () => {
  void router.push({ name: 'CreateLiquidationRequest' })
}
const getLiquidationRequest = (id: any) => {
  void router.push({ name: 'GetLiquidationRequest', params: { id } })
}
const updateLiquidationRequest = (id: any) => {
  void router.push({ name: 'UpdateLiquidationRequest', params: { id } })
}

/* Eliminar Solicitud de liquidacion */
const removeLiquidationRequest = (values: any) => {
  titleModal.value = t('liquidations.list.actionStatus')
  descriptionModal.value = t('modal.description')
  typeAlertModal.value = 'question'
  showModal.value = true
  liquidationRequestForDelete.value = values.id
  isDelete.value = true
}
const deleteConfirm = async () => {
  loading.value = true
  messageLoading.value = ''
  showModal.value = false
  await deleteLiquidationRequest(liquidationRequestForDelete.value)
    .then((response: any) => {
      notifications.addNotification({
        title: t('notification.removed'),
        text: t('liquidations.notification.action', [
          t('notification.wasRemoved')
        ]),
        type: 'success',
        time: 10000
      })
      void liquidationRequests()
    })
    .catch((error: any) => {
      notificationError(error, 'liquidations', t, notifications, loading)
    })
}

/* Generar reporte */
const downloadList = async () => {
  try {
    loading.value = true
    messageLoading.value = t('loading.report')
    urlFile.value = null
    const data = {
      code: 'configurations/entity-table',
      parameters: {
        query: JSON.stringify(payload.value),
        fields: payload.value.fields.filter(
          (field: any) =>
            !field.includes('.id') && !field.includes('status_code')
        ),
        headers: columns.value
          .filter((item: any) => item.title !== '')
          .map((item: any) => item.title),
        module: t('liquidations.list.title', 1)
      }
    }
    const id = await generateReportList(data)
    refresh.value = setInterval(async () => {
      const response = await getReportRequest(id)
      if (response.status === 'Completado') {
        urlFile.value = response.url
        descriptionModal.value = ''
        typeAlertModal.value = 'success'
        titleModal.value = t('reports.notification.reportGenerated')
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      } else if (response.status === 'Error') {
        typeAlertModal.value = 'error'
        titleModal.value = t('reports.notification.reportNoGenerated')
        descriptionModal.value = response.errors
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      }
    }, 5000)
  } catch (error: any) {
    notificationError(error, 'liquidations', t, notifications, loading)
  }
}

/* Cerrar el componente Modal */
const closeModal = () => {
  showModal.value = false
  urlFile.value = null
}

/* Listar las Solicitud de liquidacion */
const liquidationRequests = async () => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    const data: any = await getLiquidationRequests(
      offset.value,
      perPage.value,
      sortBy.value,
      search.value === '' ? undefined : search.value
    )
    rows.value = data[0].results
    totalRows.value = data[0].totalItems
    payload.value = data[1]
    loading.value = false
  } catch (error: any) {
    notificationError(error, 'liquidations', t, notifications, loading)
  }
}
</script>
<template>
  <DLoading v-show="loading" :message="messageLoading" />
  <DModal
    :open="showModal"
    :typeAlert="typeAlertModal"
    :title="titleModal"
    :description="descriptionModal"
    :acceptQuestion="
      t('modal.acceptQuestion', {
        action: t('table.action.delete')
      })
    "
    @closeModal="closeModal"
    @otherMethod="deleteConfirm">
    <template v-slot:description v-if="urlFile != null">
      <a
        class="underline tracking-widest text-blue-600"
        :href="urlFile"
        target="_blank"
        v-html="t('reports.notification.viewFile')" />
    </template>
  </DModal>
  <div class="h-full px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow"
          v-html="t('liquidations.list.title', 1)" />
        <p
          class="text-gray-500 text-shadow"
          v-html="t('liquidations.list.description')" />
      </div>
      <div class="sm:flex justify-between pt-8">
        <div class="flex mb-3 sm:mb-0 max-w-2xl sm:w-2/3 md:w-1/2 lg:w-5/12">
          <DTextField
            :placeholder="t('form.button.search') + '...'"
            :icon="'SearchLgIcon'"
            class="mr-2 w-full"
            v-model="search"
            @keyup.enter="liquidationRequests" />
          <DBtn
            @click.prevent="liquidationRequests"
            v-html="t('form.button.search')" />
        </div>
        <div class="flex gap-2">
          <DBtn
            color="excel"
            type="button"
            @click="downloadList"
            v-html="t('form.button.download')" />
          <DBtn
            :icon="'PlusIcon'"
            type="button"
            @click="createLiquidationRequest()"
            v-if="store.hasPermission('liquidation_request:create')">
            {{
              t('form.button.create', {
                form: t('liquidations.list.title', 0)
              })
            }}
          </DBtn>
        </div>
      </div>
    </div>
    <DTable
      ref="tableLiquidationRequest"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="false"
      :text-no-data="t('table.noData')"
      :text-per-page="t('table.rowsPerPage')"
      @perPage=";(perPage = $event), liquidationRequests()"
      @offset="offset = $event"
      @sort=";(sortBy = $event), liquidationRequests()">
      <template #status="data: any">
        <div
          class="flex items-center rounded-full font-semibold w-fit text-sm py-1 px-3"
          :class="
            data.status_code === 'NO_STARTED'
              ? 'bg-gray-100 text-gray-600'
              : data.status_code === 'PENDING' ||
                data.status_code === 'IN_PROGRESS'
              ? 'bg-blue-100 text-blue-600'
              : data.status_code === 'ERROR'
              ? 'bg-red-100 text-red-600'
              : 'bg-green-100 text-green-600'
          ">
          <div
            class="mr-2 w-1.5 h-1.5 rounded-full"
            :class="
              data.status_code === 'NO_STARTED'
                ? 'bg-gray-600'
                : data.status_code === 'PENDING' ||
                  data.status_code === 'IN_PROGRESS'
                ? 'bg-blue-600'
                : data.status_code === 'ERROR'
                ? 'bg-red-600'
                : 'bg-green-600'
            "></div>
          {{ t('status.' + data.status_code) }}
        </div>
      </template>
      <template #actions="data: any">
        <div class="flex py-2.5 justify-end gap-1">
          <Popper
            v-if="
              data.status_code !== 'PENDING' &&
              store.hasPermission('liquidation_request:get')
            "
            arrow
            offsetDistance="12"
            :content="t('table.action.detail')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="getLiquidationRequest(data.id)">
              <SearchMdIcon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="
              data.status_code === 'PENDING' &&
              store.hasPermission('liquidation_request:get')
            "
            arrow
            offsetDistance="12"
            :content="t('table.action.edit')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="updateLiquidationRequest(data.id)">
              <Edit03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="
              data.status_code === 'PENDING' &&
              store.hasPermission('liquidation_request:delete')
            "
            arrow
            offsetDistance="12"
            :content="t('table.action.remove')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="removeLiquidationRequest(data)">
              <Trash03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
        </div>
      </template>
      <template #listEmployees="data: any">
        <div class="flex flex-wrap">
          <div>
            {{ data.employees.slice(0, 20).join(', ') }}
          </div>
          <div
            v-show="data.employees.toString().split(',').length > 20"
            class="bg-blue-50 rounded-lg text-blue-700 text-sm font-semibold p-0.5 ml-2 h-6 margin-auto inline-block">
            {{ data.employees.toString().split(',').length - 20 }}+
          </div>
        </div>
      </template>
    </DTable>
  </div>
</template>
